import { Button } from "@mui/material";
import { ExpandMore, GroupAdd } from "@mui/icons-material";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { navItems, resourcesItems } from "../../utils/NavData";

export const Header = () => {
  const [toggleopen, setToggleOpen] = useState(true);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(location.pathname);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setActive(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setResourcesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isResourceActive = resourcesItems.some((r) => r.path === active);

  const closeAll = () => {
    setToggleOpen(true);
    setResourcesOpen(false);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <Fragment>
      <nav className="bg-white sticky top-0 z-50 border-gray-200 h-20 lg:h-20 md:py-0 px-2 sm:px-4 shadow-lg rounded font-abel">
        <div className="container flex flex-wrap justify-between items-center md:px-0 lg:px-20">
          <a href="/" className="flex items-center py-4 md:py-0 md:-mt-5">
            <img
              src="/salv.png"
              className="md:-mr-20 lg:mr-0 h-12 w-20 md:h-12 md:w-20 lg:h-15 lg:w-24"
              alt="Church Logo"
            />
            <div className="self-center font-aref ml-2 md:ml-20 lg:ml-2 text-xl lg:text-2xl font-bold whitespace-nowrap text-secondary">
              Salvation Parish
            </div>
          </a>

          {/* Mobile hamburger */}
          <button
            onClick={() => setToggleOpen(!toggleopen)}
            type="button"
            className="cursor-pointer inline-flex items-center p-2 ml-3 text-sm text-gray-500 rounded-lg md:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            <span className="sr-only">Toggle menu</span>
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Nav links */}
          <div
            className={`${
              toggleopen ? "hidden" : "block"
            } w-full md:block md:w-auto md:-mt-4`}
          >
            <ul className="absolute bg-white md:bg-transparent w-full md:w-fit left-0 md:relative flex flex-col md:py-12 md:flex-row uppercase md:space-x-4 lg:space-x-8 md:text-sm md:font-medium items-center md:-ml-20 md:mt-0 lg:mt-0">
              {navItems.map((item, index) => (
                <li key={index}>
                  <Link
                    onClick={() => {
                      setActive(item.path);
                      closeAll();
                    }}
                    to={item.path}
                    className={`${
                      active === item.path ? "text-secondary" : "text-primary"
                    } hover-underline-animation text-base py-2 pr-4 pl-3 border-b border-gray-100 hover:bg-gray-50 md:hover:bg-transparent md:border-0 md:hover:text-mhash md:p-0`}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}

              {/* Resources dropdown */}
              <li className="relative" ref={dropdownRef}>
                {/* Desktop: hover trigger */}
                <button
                  onClick={() => setResourcesOpen(!resourcesOpen)}
                  className={`${
                    isResourceActive ? "text-secondary" : "text-primary"
                  } hover-underline-animation flex items-center gap-1 text-base py-2 pr-4 pl-3 border-b border-gray-100 hover:bg-gray-50 md:hover:bg-transparent md:border-0 md:hover:text-mhash md:p-0 uppercase font-medium cursor-pointer`}
                >
                  Resources
                  <ExpandMore
                    fontSize="small"
                    className={`transition-transform duration-200 ${
                      resourcesOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Dropdown panel */}
                {resourcesOpen && (
                  <ul className="md:absolute left-0 md:top-full mt-1 bg-white md:shadow-xl md:rounded-lg md:border border-gray-100 md:min-w-[150px] z-50 py-1">
                    {resourcesItems.map((item) => (
                      <li key={item.slug}>
                        <Link
                          to={item.path}
                          onClick={() => {
                            setActive(item.path);
                            closeAll();
                          }}
                          className={`${
                            active === item.path
                              ? "text-secondary bg-gray-50"
                              : "text-primary"
                          } block px-4 py-2 text-sm hover:bg-gray-50 hover:text-secondary whitespace-nowrap font-abel normal-case`}
                        >
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>

              {/* Become a Member button */}
              <li className="-mt-2 pt-2 pb-2 md:pb-0 md:pt-0">
                <Button
                  variant="outlined"
                  onClick={() => {
                    navigate("/join");
                    setToggleOpen(true);
                  }}
                  startIcon={<GroupAdd style={{ fontSize: 16 }} />}
                  className="!rounded-full !font-abel !text-xs !uppercase !tracking-wider !h-8 lg:!h-9 !px-4"
                  style={{
                    borderColor: "#000c66",
                    color: "#000c66",
                    fontFamily: "Abel",
                    whiteSpace: "nowrap",
                  }}
                >
                  Become a Member
                </Button>
              </li>

              {/* Donations button */}
              <li className="-mt-2 pt-2 pb-2 md:pb-0 md:pt-0">
                <Button
                  variant="contained"
                  color="custom"
                  className="btn2 mt-12 h-8 lg:h-10 relative border md:text-lg lg:!text-xl !rounded-full border-white uppercase font-abel font-semibold tracking-wider leading-none overflow-hidden hover:text-teal-600"
                  onClick={() => {
                    window.open("https://paystack.com/pay/salvation-donation");
                    setToggleOpen(true);
                  }}
                >
                  <span className="absolute inset-0 bg-secondary"></span>
                  <span className="absolute inset-0 flex justify-center items-center">
                    Donations
                  </span>
                  Donations
                </Button>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </Fragment>
  );
};
